import { fileUrl } from '../lib/utils';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import {
    FileText,
    Download,
    Search,
    Filter,
    Receipt,
    FileCheck,
    Eye,
    ExternalLink,
    FolderOpen,
    HardDrive,
    RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminFiles = () => {
    const { api } = useAuth();
    const [files, setFiles] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');

    useEffect(() => {
        fetchFiles();
        fetchStats();
    }, [typeFilter]);

    const fetchFiles = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (typeFilter && typeFilter !== 'all') {
                params.append('file_type', typeFilter);
            }
            const response = await api.get(`/admin/files?${params.toString()}`);
            setFiles(response.data);
        } catch (error) {
            console.error('Failed to fetch files:', error);
            toast.error('Erreur lors du chargement des fichiers');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await api.get('/admin/files/stats');
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    };

    const handleRefresh = () => {
        fetchFiles();
        fetchStats();
        toast.success('Liste actualisée');
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            submitted: { label: 'Soumise', variant: 'secondary' },
            validated: { label: 'Validée', variant: 'default' },
            refused: { label: 'Refusée', variant: 'destructive' },
            reimbursed: { label: 'Remboursée', variant: 'outline' },
            pending: { label: 'En attente', variant: 'secondary' },
            processing: { label: 'En cours', variant: 'default' },
            quote_uploaded: { label: 'Devis déposé', variant: 'default' },
        };
        const config = statusConfig[status] || { label: status, variant: 'secondary' };
        return <Badge variant={config.variant}>{config.label}</Badge>;
    };

    const getCategoryLabel = (category) => {
        const categories = {
            transport: 'Transport',
            meal: 'Repas',
            accommodation: 'Hébergement',
            supplies: 'Fournitures',
            other: 'Autre'
        };
        return categories[category] || category || '-';
    };

    const getFileUrl = fileUrl;

    const filteredFiles = files.filter(file => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            (file.user_name && file.user_name.toLowerCase().includes(query)) ||
            (file.company_name && file.company_name.toLowerCase().includes(query)) ||
            (file.description && file.description.toLowerCase().includes(query)) ||
            (file.file_name && file.file_name.toLowerCase().includes(query))
        );
    });

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold font-heading text-foreground">
                        Gestion des fichiers
                    </h1>
                    <p className="text-muted-foreground mt-2 text-sm md:text-base">
                        Tous les justificatifs et documents téléchargés
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={handleRefresh}
                    className="rounded-full border-border/40"
                    data-testid="refresh-files-btn"
                >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser
                </Button>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-xl bg-blue-500/10">
                                    <Receipt className="w-6 h-6 text-blue-500" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Notes de frais</p>
                                    <p className="text-2xl font-bold text-foreground">{stats.expense_receipts}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-xl bg-purple-500/10">
                                    <FileCheck className="w-6 h-6 text-purple-500" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Devis</p>
                                    <p className="text-2xl font-bold text-foreground">{stats.quote_files}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-xl bg-green-500/10">
                                    <FolderOpen className="w-6 h-6 text-green-500" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Total fichiers</p>
                                    <p className="text-2xl font-bold text-foreground">{stats.total_files}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-xl bg-orange-500/10">
                                    <HardDrive className="w-6 h-6 text-orange-500" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Sur le disque</p>
                                    <p className="text-2xl font-bold text-foreground">{stats.files_on_disk?.total || 0}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Filters */}
            <Card>
                <CardContent className="p-5">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Rechercher par nom, entreprise, fichier..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 rounded-lg border-border/40 focus:border-primary/50"
                                data-testid="files-search-input"
                            />
                        </div>
                        <Select value={typeFilter} onValueChange={setTypeFilter}>
                            <SelectTrigger className="w-full sm:w-48 rounded-lg border-border/40" data-testid="type-filter">
                                <Filter className="w-4 h-4 mr-2" />
                                <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les types</SelectItem>
                                <SelectItem value="expense">Notes de frais</SelectItem>
                                <SelectItem value="quote">Devis</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Files Table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-6 space-y-4">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <Skeleton key={i} className="h-16 w-full" />
                            ))}
                        </div>
                    ) : filteredFiles.length === 0 ? (
                        <div className="text-center py-16">
                            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-40" />
                            <p className="text-muted-foreground">Aucun fichier trouvé</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="border-b border-border/40">
                                    <TableHead className="pl-6 text-muted-foreground font-semibold">Type</TableHead>
                                    <TableHead className="text-muted-foreground font-semibold">Fichier</TableHead>
                                    <TableHead className="text-muted-foreground font-semibold">Utilisateur</TableHead>
                                    <TableHead className="hidden md:table-cell text-muted-foreground font-semibold">Entreprise</TableHead>
                                    <TableHead className="hidden lg:table-cell text-muted-foreground font-semibold">Détails</TableHead>
                                    <TableHead className="text-muted-foreground font-semibold">Statut</TableHead>
                                    <TableHead className="text-muted-foreground font-semibold">Date</TableHead>
                                    <TableHead className="pr-6 w-[100px]"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredFiles.map((file) => (
                                    <TableRow 
                                        key={`${file.type}-${file.id}`} 
                                        className="border-b border-border/40 hover:bg-muted/50 transition-colors"
                                        data-testid={`file-row-${file.id}`}
                                    >
                                        <TableCell className="pl-6 py-4">
                                            <Badge 
                                                variant={file.type === 'expense' ? 'default' : 'secondary'}
                                                className="font-medium"
                                            >
                                                {file.type === 'expense' ? (
                                                    <Receipt className="w-3 h-3 mr-1" />
                                                ) : (
                                                    <FileCheck className="w-3 h-3 mr-1" />
                                                )}
                                                {file.type_label}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="py-4">
                                            <div className="flex items-center gap-2">
                                                <FileText className="w-4 h-4 text-muted-foreground" />
                                                <span className="text-sm font-medium text-foreground truncate max-w-[200px]">
                                                    {file.file_name || 'Fichier'}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="py-4 text-foreground">
                                            {file.user_name}
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell py-4 text-muted-foreground">
                                            {file.company_name || '-'}
                                        </TableCell>
                                        <TableCell className="hidden lg:table-cell py-4">
                                            {file.type === 'expense' ? (
                                                <div className="text-sm">
                                                    <span className="font-medium text-foreground">{formatCurrency(file.amount)}</span>
                                                    <span className="text-muted-foreground ml-2">({getCategoryLabel(file.category)})</span>
                                                </div>
                                            ) : (
                                                <span className="text-sm text-muted-foreground truncate max-w-[200px] block">
                                                    {file.description || '-'}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="py-4">
                                            {getStatusBadge(file.status)}
                                        </TableCell>
                                        <TableCell className="py-4 text-sm text-muted-foreground">
                                            {formatDate(file.created_at)}
                                        </TableCell>
                                        <TableCell className="pr-6 py-4">
                                            <div className="flex items-center gap-1">
                                                {file.file_url && (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 rounded-full hover:bg-muted/80"
                                                            onClick={() => window.open(getFileUrl(file.file_url), '_blank')}
                                                            title="Voir le fichier"
                                                        >
                                                            <Eye className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 rounded-full hover:bg-blue-500/10"
                                                            asChild
                                                            title="Télécharger"
                                                        >
                                                            <a href={getFileUrl(file.file_url)} download target="_blank" rel="noopener noreferrer">
                                                                <Download className="w-4 h-4 text-muted-foreground hover:text-blue-500" />
                                                            </a>
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* File count */}
            {!loading && filteredFiles.length > 0 && (
                <p className="text-sm text-muted-foreground text-center">
                    {filteredFiles.length} fichier(s) affiché(s)
                </p>
            )}
        </div>
    );
};

export default AdminFiles;
